import { IndicationItem } from 'features/indicationTriage/api/indicationTriageApi'
import flatten from 'lodash/flatten'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { PathString } from 'react-hook-form'

export interface IndicationDetailCardLeaf {
  id: PathString
  component: string
  config?: Record<string, unknown>
}

export interface IndicationDetailCardGroup {
  title: string
  children: (IndicationDetailCardGroup | IndicationDetailCardLeaf)[]
}

export interface IndicationDetailCardProps<C = never> {
  config?: C
}

export const DEFAULT_DETAIL_CARD_CONFIG: (
  | IndicationDetailCardLeaf
  | IndicationDetailCardGroup
)[] = [
  {
    id: 'overview',
    component: 'IndicationOverview',
  },
]

/**
 * Walk the configs depth-first - used to load the components.
 */
function walkCardsDepthFirst<T>(
  rootCard: IndicationDetailCardGroup | IndicationDetailCardLeaf,
  yieldLeaf: (
    parents: IndicationDetailCardGroup[],
    leaf: IndicationDetailCardLeaf
  ) => T,
  parents?: IndicationDetailCardGroup[]
): T[] {
  if ('component' in rootCard) {
    return [yieldLeaf(parents ?? [], rootCard)]
  } else {
    return flatten(
      rootCard.children.map((subCard) => {
        return walkCardsDepthFirst(subCard, yieldLeaf, [
          ...(parents ?? []),
          rootCard,
        ])
      })
    )
  }
}

/**
 * Holds information to render the card.
 */
export type LoadedIndicationDetailCard = {
  Component: React.FC<IndicationDetailCardProps<unknown>>
  title?: (
    indicationItem: IndicationItem,
    config?: Record<string, unknown>
  ) => React.ReactNode
  width: string
  useIsEnabled?: (indicationItem: IndicationItem | undefined) => boolean
}
/**
 * The return type of the hook.
 */
export type IndicationCardsSwr = {
  cardComponents: ReadonlyMap<
    string,
    LoadedIndicationDetailCard | null | 'Error'
  >
  isLoading: boolean
}

/**
 * A hook to traverse the card configs and load the relevant components
 * for display.
 */
export function useIndicationCards(
  rootCards: ReadonlyArray<IndicationDetailCardGroup | IndicationDetailCardLeaf>
): IndicationCardsSwr {
  const cardNameToLoadedCard = useRef<
    Map<string, LoadedIndicationDetailCard | null | 'Error'>
  >(new Map())
  const [loadingCards, setLoadingCards] = useState<ReadonlySet<string>>(
    new Set()
  )

  const cardsToLoad = useMemo(
    () =>
      flatten(
        rootCards.map((rootCard) =>
          walkCardsDepthFirst(rootCard, (perents, leaf) => {
            return {
              id: leaf.id,
              card: leaf.component,
            }
          })
        )
      ),
    [rootCards]
  )
  useEffect(() => {
    cardsToLoad.forEach(async ({ card: leafCard }) => {
      if (!Object.keys(cardNameToLoadedCard).includes(leafCard)) {
        cardNameToLoadedCard.current.set(leafCard, null)
        setLoadingCards((prev) => new Set(prev).add(leafCard))
        try {
          const { CardView, Title, Width, useIsEnabled } = await import(
            `./cards/${leafCard}Card.tsx`
          )
          cardNameToLoadedCard.current.set(leafCard, {
            Component: React.memo(CardView),
            title: Title,
            width: Width ?? '200px',
            useIsEnabled: useIsEnabled,
          })
        } catch (error) {
          console.error(error)
          cardNameToLoadedCard.current.set(leafCard, 'Error')
        } finally {
          setLoadingCards((prev) => {
            const newSet = new Set(prev)
            newSet.delete(leafCard)
            return newSet
          })
        }
      }
    })
  }, [cardsToLoad])

  const loadedCards: ReadonlyMap<
    string,
    LoadedIndicationDetailCard | null | 'Error'
  > = useMemo(() => {
    const lcMap: Map<string, LoadedIndicationDetailCard | null | 'Error'> =
      new Map()
    cardsToLoad.forEach((card) => {
      if (loadingCards.has(card.card)) {
        return
      }
      lcMap.set(card.id, cardNameToLoadedCard.current.get(card.card) ?? null)
    })
    return lcMap
  }, [cardsToLoad, loadingCards])

  return useMemo(() => {
    return {
      cardComponents: loadedCards,
      isLoading: !!loadingCards.size,
    }
  }, [loadedCards, loadingCards.size])
}
